import { useState, useCallback, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import {
  ClipboardDocumentIcon,
  CheckIcon,
  ArrowDownTrayIcon,
  DocumentMagnifyingGlassIcon,
  CloudArrowUpIcon,
  ChevronRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  collectDiagnosticReport,
  type DiagnosticReport,
  type DiagnosticReportMeta,
  type DiagnosticCategory,
} from "@/shared/services/diagnostic";
import { safeClipboardWrite } from "@/utils/clipboard";
import { submitDiagnosticReport } from "@/shared/api/pref-storage";
import { useIsPollingMode } from "@/shared/api/newsquawk-headlines";
import { useIsFirehosePollingMode } from "@/shared/api/firehose/useIsFirehosePollingMode";
import { useSquawkPlayer } from "@/layout-components/squawk-player/SquawkPlayerContext";
import { PLAYER_PRIORITY } from "@/layout-components/squawk-player/types";

const HEADLINES_SSE_ENDPOINT = `${import.meta.env.VITE_NOTIFICATIONS_BASE_URL}/api/stream`;
const FIREHOSE_SSE_ENDPOINT = `${import.meta.env.VITE_FIREHOSE_BASE_URL}/stream`;
const APP_DOMAIN = window.location.origin;

// Friendly labels for squawk transports. `connectionType` is the user's saved
// preference; `fallbackOverride` is the degraded transport we were forced onto.
const SQUAWK_TRANSPORT_LABELS: Record<string, string> = {
  web_rtc: "WebRTC",
  web_sockets: "WebSockets (MSE)",
  shoutcast: "Shoutcast",
  mse: "WebSockets (MSE)",
  webrtc: "WebRTC",
  v2: "Squawk v2",
};
const squawkTransportLabel = (key: string | null | undefined): string =>
  (key && SQUAWK_TRANSPORT_LABELS[key]) || "your preferred connection";

const DETAILS_TEXT = `Newsquawk — Please allow SSE streaming endpoints

Newsquawk delivers real-time financial headlines and stories via Server-Sent Events (SSE). Our portal has detected that these connections are being blocked or dropped by the network, causing it to fall back to periodic polling (up to 2–3 second delay instead of sub-second).

Please allowlist the following so SSE connections can stay open:

  Domain: ${APP_DOMAIN}
  Headlines SSE endpoint: ${HEADLINES_SSE_ENDPOINT}
  Firehose SSE endpoint: ${FIREHOSE_SSE_ENDPOINT}

Technical details:
  - Protocol: HTTPS (port 443)
  - Method: Standard HTTP GET with a long-lived response (text/event-stream)
  - No WebSocket upgrade or special ports required
  - The connection just needs to be allowed to remain open by proxies, firewalls, and SSL inspection appliances`;

const ALWAYS_INCLUDED_CATEGORIES: DiagnosticCategory[] = [
  "environment",
  "appLifecycle",
  "widgetState",
  "sentryReplay",
  "eventTimeline",
];

const ISSUE_OPTIONS: {
  id: string;
  label: string;
  description: string;
  categories: DiagnosticCategory[];
}[] = [
  {
    id: "audioIssue",
    label: "Audio not working or cutting out",
    description:
      "The squawk player won't connect, audio drops, or you hear no sound",
    categories: ["audioPlayer"],
  },
  {
    id: "headlinesIssue",
    label: "Headlines not updating",
    description:
      "Headlines appear delayed, stuck, or the feed shows a Polling indicator",
    categories: ["sseState", "networkErrors"],
  },
  {
    id: "otherIssue",
    label: "Other issue",
    description: "Something else not listed above",
    categories: ["sseState", "networkErrors", "audioPlayer"],
  },
];

function eventLevelColor(level: string): string {
  switch (level) {
    case "error":
      return "text-red-600 dark:text-red-400";
    case "warn":
      return "text-amber-600 dark:text-amber-400";
    default:
      return "text-gray-600 dark:text-gray-300";
  }
}

function downloadJSON(report: DiagnosticReport) {
  const blob = new Blob([JSON.stringify(report, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `newsquawk-diagnostic-${report.reportId}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

function NetworkRequirementsBlock() {
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
        Network requirements
      </h3>
      <div className="mt-3 space-y-2">
        <div className="rounded-md bg-gray-100 dark:bg-gray-700/50 px-4 py-3">
          <span className="text-2xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Domain
          </span>
          <code className="block text-xs font-mono text-gray-800 dark:text-gray-200 break-all select-all mt-1">
            {APP_DOMAIN}
          </code>
        </div>
        <div className="rounded-md bg-gray-100 dark:bg-gray-700/50 px-4 py-3">
          <span className="text-2xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Headlines SSE Endpoint
          </span>
          <code className="block text-xs font-mono text-gray-800 dark:text-gray-200 break-all select-all mt-1">
            {HEADLINES_SSE_ENDPOINT}
          </code>
        </div>
        <div className="rounded-md bg-gray-100 dark:bg-gray-700/50 px-4 py-3">
          <span className="text-2xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Firehose SSE Endpoint
          </span>
          <code className="block text-xs font-mono text-gray-800 dark:text-gray-200 break-all select-all mt-1">
            {FIREHOSE_SSE_ENDPOINT}
          </code>
        </div>
      </div>
    </div>
  );
}

function ConnectionDetailsBlock() {
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
        Connection details
      </h3>
      <ul className="mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-300 list-disc list-inside">
        <li>HTTPS (port 443)</li>
        <li>
          Standard HTTP GET with a long-lived response (text/event-stream)
        </li>
        <li>No WebSocket upgrade or special ports required</li>
        <li>
          The connection just needs to be allowed to remain open by proxies,
          firewalls, and SSL inspection appliances
        </li>
      </ul>
    </div>
  );
}

function SseStatusPill({ polling }: { polling: boolean }) {
  if (polling) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-200">
        <span className="size-1.5 rounded-full bg-amber-500" />
        Polling fallback
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 dark:bg-green-900/30 px-2 py-0.5 text-xs font-medium text-green-800 dark:text-green-300">
      <span className="size-1.5 rounded-full bg-green-500" />
      Connected
    </span>
  );
}

const DEFAULT_META: DiagnosticReportMeta = {
  affectedServices: {
    all: true,
    textHeadlines: false,
    squawk: false,
    finX: false,
    other: false,
    otherDescription: "",
  },
  updatesAfterRefresh: false,
  worksInIncognito: false,
  workedBefore: false,
};

function Troubleshooting() {
  const isHeadlinesPolling = useIsPollingMode();
  const isFirehosePolling = useIsFirehosePollingMode();
  const isAnyPolling = isHeadlinesPolling || isFirehosePolling;
  const { fallbackOverride, didAutoFallback, preferredPlayer } =
    useSquawkPlayer();
  // Describe an auto-fallback accurately. It's only a "downgrade" (higher latency)
  // when the active transport ranks below the user's preferred one in
  // PLAYER_PRIORITY. Falling back onto WebRTC (e.g. from WebSockets) is actually an
  // upgrade — so we must not warn about latency or call it "degraded" there.
  const fallbackActiveLabel = squawkTransportLabel(fallbackOverride);
  const fallbackPreferredLabel = squawkTransportLabel(preferredPlayer);
  const fallbackIsDowngrade =
    PLAYER_PRIORITY.indexOf(fallbackOverride ?? "") >
    PLAYER_PRIORITY.indexOf(preferredPlayer ?? "");
  const [copied, setCopied] = useState(false);
  const [requirementsExpanded, setRequirementsExpanded] = useState(false);
  const [selectedIssues, setSelectedIssues] = useState<Set<string>>(
    () => new Set(ISSUE_OPTIONS.map((o) => o.id))
  );
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState<DiagnosticReport | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [meta, setMeta] = useState<DiagnosticReportMeta>(DEFAULT_META);

  const uploadMutation = useMutation({
    mutationFn: (r: DiagnosticReport) => submitDiagnosticReport(r),
  });

  const reportWithMeta = useMemo<DiagnosticReport | null>(
    () => (report ? { ...report, meta } : null),
    [report, meta]
  );

  const copyDetails = async () => {
    await safeClipboardWrite(DETAILS_TEXT);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleIssue = (id: string) => {
    setSelectedIssues((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const generateReport = useCallback(async () => {
    setGenerating(true);
    setReport(null);
    setMeta(DEFAULT_META);
    uploadMutation.reset();
    try {
      const categories = new Set<DiagnosticCategory>(ALWAYS_INCLUDED_CATEGORIES);
      for (const issue of ISSUE_OPTIONS) {
        if (selectedIssues.has(issue.id)) {
          for (const cat of issue.categories) {
            categories.add(cat);
          }
        }
      }
      const r = await collectDiagnosticReport(categories);
      setReport(r);
      setDialogOpen(true);
    } catch (err) {
      console.error("Failed to generate diagnostic report:", err);
    } finally {
      setGenerating(false);
    }
  }, [selectedIssues, uploadMutation]);

  const setAffectedScope = (scope: "all" | "some") => {
    setMeta((m) => ({
      ...m,
      affectedServices: {
        ...m.affectedServices,
        all: scope === "all",
        ...(scope === "all"
          ? { textHeadlines: false, squawk: false, finX: false, other: false }
          : {}),
      },
    }));
  };

  const toggleAffectedService = (
    key: "textHeadlines" | "squawk" | "finX" | "other"
  ) => {
    setMeta((m) => ({
      ...m,
      affectedServices: {
        ...m.affectedServices,
        [key]: !m.affectedServices[key],
      },
    }));
  };

  return (
    <div className="divide-y divide-gray-200 dark:divide-white/10">
      {/* SSE Connectivity */}
      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
              Live Streaming (SSE)
            </h2>
            <SseStatusPill polling={isAnyPolling} />
          </div>
          <p className="mt-1 text-sm/6 text-gray-500 dark:text-gray-400">
            Headlines and stories stream in real time over Server-Sent Events.
          </p>
        </div>
        <div className="md:col-span-2">
          {isAnyPolling ? (
            <div className="space-y-6">
              <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4">
                <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                  Live streaming is unavailable on your network
                </h3>
                <p className="mt-2 text-sm text-amber-800 dark:text-amber-200">
                  SSE connections are being blocked, so we&apos;ve switched to
                  periodic polling. Stories arrive with a 2–3 second delay
                  instead of sub-second. Send the network requirements below
                  to your IT team to restore live streaming.
                </p>
                <div className="mt-3">
                  <button
                    onClick={copyDetails}
                    className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 dark:bg-amber-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 dark:hover:bg-amber-400 transition-colors cursor-pointer"
                  >
                    {copied ? (
                      <>
                        <CheckIcon className="size-3.5" />
                        Copied
                      </>
                    ) : (
                      <>
                        <ClipboardDocumentIcon className="size-3.5" />
                        Copy for IT team
                      </>
                    )}
                  </button>
                </div>
              </div>

              <NetworkRequirementsBlock />
              <ConnectionDetailsBlock />
            </div>
          ) : (
            <div>
              <button
                type="button"
                onClick={() => setRequirementsExpanded((v) => !v)}
                className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-squawk-blue dark:hover:text-squawk-blue cursor-pointer transition-colors"
              >
                <ChevronRightIcon
                  className={`size-4 transition-transform ${
                    requirementsExpanded ? "rotate-90" : ""
                  }`}
                />
                Having issues? View network requirements for IT
              </button>
              {requirementsExpanded && (
                <div className="mt-6 space-y-6">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    If a widget header shows a Polling indicator, your network
                    may be blocking SSE connections. Allowlisting the
                    following typically restores live streaming.
                  </p>
                  <NetworkRequirementsBlock />
                  <ConnectionDetailsBlock />
                  <div>
                    <button
                      onClick={copyDetails}
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-squawk-blue hover:text-squawk-blue dark:hover:text-squawk-blue transition-colors cursor-pointer"
                    >
                      {copied ? (
                        <>
                          <CheckIcon className="size-4" />
                          Copied
                        </>
                      ) : (
                        <>
                          <ClipboardDocumentIcon className="size-4" />
                          Copy for IT team
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Squawk Audio — auto-fallback notice */}
      {didAutoFallback && (
        <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
                Squawk Audio
              </h2>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-200">
                <span className="size-1.5 rounded-full bg-amber-500" />
                {fallbackIsDowngrade ? "Degraded fallback" : "Transport switched"}
              </span>
            </div>
            <p className="mt-1 text-sm/6 text-gray-500 dark:text-gray-400">
              The audio squawk connects over a real-time transport you select in
              Squawk settings.
            </p>
          </div>
          <div className="md:col-span-2">
            <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4">
              <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                Squawk audio {fallbackIsDowngrade ? "fell back to" : "switched to"}{" "}
                {fallbackActiveLabel}
              </h3>
              <p className="mt-2 text-sm text-amber-800 dark:text-amber-200">
                Your preferred squawk transport ({fallbackPreferredLabel})
                couldn&apos;t stay connected on your network, so we automatically
                switched to {fallbackActiveLabel} to keep audio playing.{" "}
                {fallbackIsDowngrade
                  ? "You may notice slightly higher latency."
                  : "It's a lower-latency transport, so audio should be unaffected."}{" "}
                This usually means a firewall or proxy is interfering with
                WebSocket/WebRTC connections — please contact us so we can
                investigate why your preferred transport failed.
              </p>
              <div className="mt-3">
                <a
                  href="mailto:support@newsquawk.com?subject=Squawk%20audio%20auto-fallback"
                  className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 dark:bg-amber-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 dark:hover:bg-amber-400 transition-colors"
                >
                  Contact support to investigate
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Diagnostic Report */}
      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
            Diagnostic Report
          </h2>
          <p className="mt-1 text-sm/6 text-gray-500 dark:text-gray-400">
            Tell us what&apos;s wrong and we&apos;ll collect the right
            diagnostic information to help support investigate.
          </p>
        </div>
        <div className="md:col-span-2 space-y-6">
          {/* Issue checkboxes */}
          <fieldset>
            <div className="flex items-center justify-between">
              <legend className="text-sm font-semibold text-gray-900 dark:text-white">
                What issue are you experiencing?
              </legend>
              <button
                type="button"
                onClick={() =>
                  setSelectedIssues((prev) =>
                    prev.size === ISSUE_OPTIONS.length
                      ? new Set()
                      : new Set(ISSUE_OPTIONS.map((o) => o.id))
                  )
                }
                className="text-xs text-squawk-blue hover:text-squawk-blue/80 cursor-pointer"
              >
                {selectedIssues.size === ISSUE_OPTIONS.length
                  ? "Unselect all"
                  : "Select all"}
              </button>
            </div>
            <div className="mt-3 space-y-3">
              {ISSUE_OPTIONS.map((issue) => (
                <label
                  key={issue.id}
                  className="flex items-start gap-3 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedIssues.has(issue.id)}
                    onChange={() => toggleIssue(issue.id)}
                    className="mt-0.5 size-4 rounded border-gray-300 dark:border-gray-600 text-squawk-blue focus:ring-squawk-blue cursor-pointer"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {issue.label}
                    </span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {issue.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </fieldset>

          {/* Generate button */}
          <button
            onClick={generateReport}
            disabled={generating}
            className="inline-flex items-center gap-2 rounded-md bg-squawk-blue px-4 py-2 text-sm font-medium text-white hover:bg-squawk-blue/80 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DocumentMagnifyingGlassIcon className="size-4" />
            {generating ? "Generating..." : "Generate Diagnostic Report"}
          </button>

          {/* Report dialog */}
          {report && (
            <Dialog
              open={dialogOpen}
              onClose={() => setDialogOpen(false)}
              className="relative z-[100] no-drag"
            >
              <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-900/80 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150"
              />
              <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
                <DialogPanel
                  transition
                  className="w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden rounded-lg bg-white dark:bg-gray-800 shadow-xl transition-all data-[closed]:translate-y-2 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[closed]:scale-95"
                >
                  <div className="flex items-start justify-between px-5 pt-5 pb-3 border-b border-gray-200 dark:border-gray-700">
                    <div>
                      <DialogTitle className="text-base font-semibold text-gray-900 dark:text-white">
                        Diagnostic report
                      </DialogTitle>
                      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        {report.reportId} ·{" "}
                        {new Date(report.generatedAt).toLocaleString()}
                      </p>
                    </div>
                    <button
                      onClick={() => setDialogOpen(false)}
                      aria-label="Close"
                      className="rounded-md p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer"
                    >
                      <XMarkIcon className="size-5" />
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
                    {/* Additional context form */}
                    <div className="space-y-5">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                          Additional context
                        </h3>
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                          Helps support narrow down the problem faster.
                        </p>
                      </div>

                      {/* Affected services */}
                      <fieldset>
                        <legend className="text-sm font-medium text-gray-900 dark:text-white">
                          Are all services affected, or only specific ones?
                        </legend>
                        <div className="mt-2 space-y-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="affected-scope"
                              checked={meta.affectedServices.all}
                              onChange={() => setAffectedScope("all")}
                              className="size-4 border-gray-300 dark:border-gray-600 text-squawk-blue focus:ring-squawk-blue cursor-pointer"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-200">
                              All services
                            </span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="affected-scope"
                              checked={!meta.affectedServices.all}
                              onChange={() => setAffectedScope("some")}
                              className="size-4 border-gray-300 dark:border-gray-600 text-squawk-blue focus:ring-squawk-blue cursor-pointer"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-200">
                              Only specific services
                            </span>
                          </label>

                          {!meta.affectedServices.all && (
                            <div className="ml-6 mt-2 space-y-2 border-l border-gray-200 dark:border-gray-700 pl-3">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={meta.affectedServices.textHeadlines}
                                  onChange={() =>
                                    toggleAffectedService("textHeadlines")
                                  }
                                  className="size-4 rounded border-gray-300 dark:border-gray-600 text-squawk-blue focus:ring-squawk-blue cursor-pointer"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-200">
                                  Text headlines
                                </span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={meta.affectedServices.squawk}
                                  onChange={() => toggleAffectedService("squawk")}
                                  className="size-4 rounded border-gray-300 dark:border-gray-600 text-squawk-blue focus:ring-squawk-blue cursor-pointer"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-200">
                                  Squawk
                                </span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={meta.affectedServices.finX}
                                  onChange={() => toggleAffectedService("finX")}
                                  className="size-4 rounded border-gray-300 dark:border-gray-600 text-squawk-blue focus:ring-squawk-blue cursor-pointer"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-200">
                                  Fin-X
                                </span>
                              </label>
                              <div className="space-y-1.5">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={meta.affectedServices.other}
                                    onChange={() => toggleAffectedService("other")}
                                    className="size-4 rounded border-gray-300 dark:border-gray-600 text-squawk-blue focus:ring-squawk-blue cursor-pointer"
                                  />
                                  <span className="text-sm text-gray-700 dark:text-gray-200">
                                    Other
                                  </span>
                                </label>
                                {meta.affectedServices.other && (
                                  <input
                                    type="text"
                                    value={meta.affectedServices.otherDescription ?? ""}
                                    onChange={(e) =>
                                      setMeta((m) => ({
                                        ...m,
                                        affectedServices: {
                                          ...m.affectedServices,
                                          otherDescription: e.target.value,
                                        },
                                      }))
                                    }
                                    placeholder="Describe the issue…"
                                    className="ml-6 block w-[calc(100%-1.5rem)] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2.5 py-1.5 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:border-squawk-blue focus:ring-1 focus:ring-squawk-blue"
                                  />
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </fieldset>

                      {/* Yes/no questions */}
                      <div className="space-y-2">
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={meta.updatesAfterRefresh}
                            onChange={(e) =>
                              setMeta((m) => ({
                                ...m,
                                updatesAfterRefresh: e.target.checked,
                              }))
                            }
                            className="mt-0.5 size-4 rounded border-gray-300 dark:border-gray-600 text-squawk-blue focus:ring-squawk-blue cursor-pointer"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-200">
                            Do updates appear after refreshing the page?
                          </span>
                        </label>
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={meta.worksInIncognito}
                            onChange={(e) =>
                              setMeta((m) => ({
                                ...m,
                                worksInIncognito: e.target.checked,
                              }))
                            }
                            className="mt-0.5 size-4 rounded border-gray-300 dark:border-gray-600 text-squawk-blue focus:ring-squawk-blue cursor-pointer"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-200">
                            Does it work in incognito / private browsing?
                          </span>
                        </label>
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={meta.workedBefore}
                            onChange={(e) =>
                              setMeta((m) => ({
                                ...m,
                                workedBefore: e.target.checked,
                              }))
                            }
                            className="mt-0.5 size-4 rounded border-gray-300 dark:border-gray-600 text-squawk-blue focus:ring-squawk-blue cursor-pointer"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-200">
                            Did it work before?
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Collapsed report summary */}
                    <Disclosure>
                      {({ open }) => (
                        <div className="rounded-md border border-gray-200 dark:border-gray-700">
                          <DisclosureButton className="flex w-full items-center justify-between px-3 py-2 text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer">
                            <span>View report summary</span>
                            <ChevronRightIcon
                              className={`size-4 transition-transform ${
                                open ? "rotate-90" : ""
                              }`}
                            />
                          </DisclosureButton>
                          <DisclosurePanel className="space-y-4 border-t border-gray-200 dark:border-gray-700 px-3 py-3">
                            {/* SSE state preview */}
                            {report.categories.sseState &&
                              report.categories.sseState.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      SSE Connection Events ({report.categories.sseState.length})
                    </h4>
                    <div className="max-h-40 overflow-y-auto rounded-md bg-gray-50 dark:bg-gray-800 p-2 space-y-1">
                      {report.categories.sseState.slice(-20).map((e, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 text-xs font-mono"
                        >
                          <span className="text-gray-400 dark:text-gray-500 shrink-0">
                            {new Date(e.timestamp).toLocaleTimeString()}
                          </span>
                          <span className={eventLevelColor(e.level)}>
                            {e.event}
                          </span>
                          {e.details && (
                            <span className="text-gray-400 dark:text-gray-500 truncate">
                              {JSON.stringify(e.details)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Event timeline preview */}
              {report.categories.eventTimeline &&
                report.categories.eventTimeline.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      Recent Events ({report.categories.eventTimeline.length})
                    </h4>
                    <div className="max-h-40 overflow-y-auto rounded-md bg-gray-50 dark:bg-gray-800 p-2 space-y-1">
                      {report.categories.eventTimeline.slice(-20).map((e, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 text-xs font-mono"
                        >
                          <span className="text-gray-400 dark:text-gray-500 shrink-0">
                            {new Date(e.timestamp).toLocaleTimeString()}
                          </span>
                          <span className={eventLevelColor(e.level)}>
                            [{e.source}] {e.event}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Network errors count */}
              {report.categories.networkErrors && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Network Errors:
                  </span>
                  <span
                    className={`text-sm font-medium ${
                      report.categories.networkErrors.length > 0
                        ? "text-red-600 dark:text-red-400"
                        : "text-green-600 dark:text-green-400"
                    }`}
                  >
                    {report.categories.networkErrors.length}
                  </span>
                </div>
              )}

              {/* Throughput preview */}
              {report.throughput && report.throughput.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                    Message Throughput (msgs/min)
                  </h4>
                  <div className="space-y-1">
                    {report.throughput.map((t) => (
                      <div
                        key={t.source}
                        className="flex items-center gap-2 text-xs"
                      >
                        <span className="text-gray-600 dark:text-gray-300 font-medium w-32 truncate">
                          {t.source}
                        </span>
                        <div className="flex gap-1">
                          {t.buckets.map((count, i) => (
                            <span
                              key={i}
                              className={`inline-block w-6 text-center rounded px-1 ${
                                count > 0
                                  ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                  : "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500"
                              }`}
                            >
                              {count}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Widget state preview */}
              {report.categories.widgetState &&
                report.categories.widgetState.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      Widget State Events ({report.categories.widgetState.length})
                    </h4>
                    <div className="max-h-40 overflow-y-auto rounded-md bg-gray-50 dark:bg-gray-800 p-2 space-y-1">
                      {report.categories.widgetState.slice(-20).map((e, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 text-xs font-mono"
                        >
                          <span className="text-gray-400 dark:text-gray-500 shrink-0">
                            {new Date(e.timestamp).toLocaleTimeString()}
                          </span>
                          <span className={eventLevelColor(e.level)}>
                            {e.event}
                          </span>
                          {e.details && (
                            <span className="text-gray-400 dark:text-gray-500 truncate">
                              {JSON.stringify(e.details)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* App lifecycle preview */}
              {report.categories.appLifecycle &&
                report.categories.appLifecycle.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      App Lifecycle ({report.categories.appLifecycle.length})
                    </h4>
                    <div className="max-h-40 overflow-y-auto rounded-md bg-gray-50 dark:bg-gray-800 p-2 space-y-1">
                      {report.categories.appLifecycle.slice(-20).map((e, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 text-xs font-mono"
                        >
                          <span className="text-gray-400 dark:text-gray-500 shrink-0">
                            {new Date(e.timestamp).toLocaleTimeString()}
                          </span>
                          <span className={eventLevelColor(e.level)}>
                            [{e.source.split(":")[0]}] {e.event}
                          </span>
                          {e.details && (
                            <span className="text-gray-400 dark:text-gray-500 truncate">
                              {JSON.stringify(e.details)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Audio player preview */}
              {report.categories.audioPlayer &&
                report.categories.audioPlayer.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      Audio Player Events ({report.categories.audioPlayer.length})
                    </h4>
                    <div className="max-h-40 overflow-y-auto rounded-md bg-gray-50 dark:bg-gray-800 p-2 space-y-1">
                      {report.categories.audioPlayer.slice(-20).map((e, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 text-xs font-mono"
                        >
                          <span className="text-gray-400 dark:text-gray-500 shrink-0">
                            {new Date(e.timestamp).toLocaleTimeString()}
                          </span>
                          <span className={eventLevelColor(e.level)}>
                            [{e.source.split(":")[0]}] {e.event}
                          </span>
                          {e.details && (
                            <span className="text-gray-400 dark:text-gray-500 truncate">
                              {JSON.stringify(e.details)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                            {/* Sentry replay */}
                            {report.categories.sentryReplay && (
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                  Sentry Replay:
                                </span>
                                {report.categories.sentryReplay.replayActive ? (
                                  <span className="text-xs text-green-600 dark:text-green-400 font-mono">
                                    {report.categories.sentryReplay.replayId}
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    No active replay
                                  </span>
                                )}
                              </div>
                            )}
                          </DisclosurePanel>
                        </div>
                      )}
                    </Disclosure>
                  </div>

                  {/* Delivery actions */}
                  <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
                    <button
                      onClick={() =>
                        reportWithMeta && uploadMutation.mutate(reportWithMeta)
                      }
                      disabled={uploadMutation.isPending || uploadMutation.isSuccess}
                      className="inline-flex items-center gap-1.5 rounded-md bg-squawk-blue px-4 py-2 text-sm font-medium text-white hover:bg-squawk-blue/80 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {uploadMutation.isPending ? (
                        <>Sending...</>
                      ) : uploadMutation.isSuccess ? (
                        <>
                          <CheckIcon className="size-4" />
                          Sent
                        </>
                      ) : (
                        <>
                          <CloudArrowUpIcon className="size-4" />
                          Send to Support
                        </>
                      )}
                    </button>
                    {uploadMutation.isError && (
                      <span className="text-xs text-red-600 dark:text-red-400">
                        Failed to send — try downloading instead
                      </span>
                    )}
                    <button
                      onClick={() => reportWithMeta && downloadJSON(reportWithMeta)}
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-squawk-blue hover:text-squawk-blue dark:hover:text-squawk-blue transition-colors cursor-pointer"
                    >
                      <ArrowDownTrayIcon className="size-4" />
                      Download JSON
                    </button>
                  </div>
                </DialogPanel>
              </div>
            </Dialog>
          )}
        </div>
      </div>

      {/* Contact Support */}
      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
            Contact Support
          </h2>
          <p className="mt-1 text-sm/6 text-gray-500 dark:text-gray-400">
            If the issue persists after allowlisting the endpoint, our support
            team can help diagnose the problem.
          </p>
        </div>
        <div className="self-center">
          <a
            href="mailto:support@newsquawk.com"
            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:border-squawk-blue hover:text-squawk-blue dark:hover:text-squawk-blue transition-colors"
          >
            Email support@newsquawk.com
          </a>
        </div>
      </div>
    </div>
  );
}

export default Troubleshooting;
